// Dashboard (wireframe 01) — 6 KPI cards · 3-panel middle row
// (Earned value · Phase progress · Risk matrix) · 2-panel bottom row
// (Top critical tasks · Recent canonical changes) · right-pin-pane
// (Pinned + Quick actions). UI_SPEC §3.11.

import { Fragment, useMemo } from "react";
import { Link } from "react-router-dom";
import { useShell, useRightPane } from "../components/ShellContext.js";
import { useTool } from "../lib/useTool.js";
import { ErrorState, Skeleton } from "./common.js";

type DashboardData = {
  pct_complete: number | null;
  project_end_p50: string | null;
  project_end_deterministic: string | null;
  project_start: string | null;
  cpi: number | null;
  spi: number | null;
  cpi_subtext: string | null;
  spi_subtext: string | null;
  evm_data_status: {
    actuals_present: boolean;
    progress_present: boolean;
    earning_rules_present: boolean;
    task_budgets_present: boolean;
    schedule_present: boolean;
    before_project_start: boolean;
  } | null;
  open_risk_count: number;
  high_risk_count: number;
  critical_task_count: number | null;
  total_task_count: number;
  phase_progress: Array<{
    id: string;
    code: string | null;
    name: string;
    completion_pct: number | null;
  }>;
  risk_matrix: number[][];
  risk_matrix_ids: string[][][];
  risk_matrix_total: number;
  top_critical_tasks: Array<{
    id: string;
    name: string;
    slack_days: number;
    duration_days: number;
  }>;
  recent_changes: Array<{
    short: string;
    subject: string;
    ago: string;
    kind: string;
  }>;
  headline_risk: {
    id: string;
    title: string;
    severity_label: string;
    likelihood: number;
    impact: number;
    severity: number;
  } | null;
  monte_carlo: {
    p10: string | null;
    p50: string | null;
    p80: string | null;
    p95: string | null;
    simulations: number | null;
  } | null;
  top_open_risks: Array<{
    id: string;
    title: string;
    severity: number;
    severity_label: string;
    likelihood: number;
    impact: number;
    category: string | null;
    owner_role: string | null;
  }>;
  planned_value_curve: Array<{
    date: string;
    cumulative_days: number;
    cumulative_pct: number;
  }>;
};

export default function DashboardView() {
  const { project, openChat } = useShell();
  const { env, loading, error, refetch } = useTool("project_dashboard", { project });
  const data = (env?.data ?? {}) as Partial<DashboardData>;

  // Right pin pane — Pinned (Monte Carlo P-curve + top-3 open risks) + Quick actions.
  const mc = data.monte_carlo ?? null;
  const topRisks = data.top_open_risks ?? [];
  useRightPane(
    useMemo(() => (
      <div className="space-y-4">
        <section>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Pinned</div>
          <div className="mt-2 space-y-2">
            <MonteCarloCard mc={mc} />
            {topRisks.length > 0 && (
              <div className="rounded border border-slate-200 p-2 text-xs">
                <div className="mb-1 flex items-center justify-between">
                  <div className="font-medium">Top open risks</div>
                  <Link to="/risk" className="text-[10px] text-blue-600 hover:underline">all →</Link>
                </div>
                <ul className="space-y-1.5">
                  {topRisks.map((r) => (
                    <li key={r.id}
                        className="rounded border border-slate-100 bg-slate-50 px-1.5 py-1"
                        title={`${r.category ?? "(no category)"} · owner: ${r.owner_role ?? "?"}`}>
                      <div className="truncate">
                        <span className="font-mono text-slate-700">{r.id}</span> · {r.title}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1.5">
                        <span className={severityChipClasses(r.severity_label)}>
                          {r.severity_label}
                        </span>
                        <span className="text-[10px] text-slate-500">
                          L={r.likelihood} · I={r.impact} · sev {r.severity}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>

        <section>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Quick actions</div>
          <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
            <button type="button"
                    className="rounded border border-slate-200 px-2 py-1 hover:bg-slate-50"
                    onClick={() => openChat("Rebuild the project: run build_all and re-import all three apps.")}
            >Rebuild</button>
            <button type="button"
                    className="rounded border border-slate-200 px-2 py-1 hover:bg-slate-50"
                    onClick={() => openChat("Backup all three apps now.")}
            >Backup</button>
            <button type="button"
                    className="rounded border border-slate-200 px-2 py-1 hover:bg-slate-50"
                    onClick={() => openChat("Verify the state of all three apps against canonical.")}
            >Verify</button>
            <button type="button"
                    className="rounded border border-slate-200 px-2 py-1 hover:bg-slate-50"
                    onClick={() => openChat("Run the smoke test suite against all three apps.")}
            >Smoke test</button>
          </div>
        </section>
      </div>
    ), [mc, topRisks, openChat]),
  );

  if (loading) return <Skeleton lines={6} />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  return (
    <div className="space-y-3">
      <KpiRow data={data} />
      <MiddleRow data={data} />
      <BottomRow data={data} />
    </div>
  );
}

// ---- KPI row -------------------------------------------------------------

function KpiRow({ data }: { data: Partial<DashboardData> }) {
  const pct = data.pct_complete != null
    ? `${Math.round(data.pct_complete * 100)}%`
    : "—";
  const endDate = data.project_end_p50 ?? data.project_end_deterministic ?? "—";
  // When both are available, compute drift between deterministic CPM and P50
  // so the sub-label tells the user something useful.
  let endHint: string;
  if (data.project_end_p50 && data.project_end_deterministic) {
    const driftDays = Math.round(
      (new Date(data.project_end_p50).getTime() -
        new Date(data.project_end_deterministic).getTime()) /
        86_400_000,
    );
    endHint = driftDays === 0
      ? "P50 = deterministic"
      : driftDays > 0
        ? `+${driftDays}d vs deterministic`
        : `${driftDays}d vs deterministic`;
  } else {
    endHint = data.project_end_p50
      ? "P50 (Monte Carlo)"
      : (data.project_end_deterministic ? "deterministic" : "no schedule");
  }
  const cpi = data.cpi != null ? data.cpi.toFixed(2) : "—";
  const spi = data.spi != null ? data.spi.toFixed(2) : "—";
  const open = data.open_risk_count ?? 0;
  const high = data.high_risk_count ?? 0;
  const crit = data.critical_task_count ?? null;
  const total = data.total_task_count ?? 0;

  return (
    <section className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      <KpiCard label="% complete" value={pct}
               sub={data.pct_complete != null ? "elapsed vs project window" : "no schedule"}
               subTone="muted" />
      <KpiCard label="Project end (P50)" value={endDate} sub={endHint}
               subTone={data.project_end_p50 ? "muted" : "amber"} />
      <KpiCard label="CPI" value={cpi}
               sub={data.cpi_subtext ?? (data.cpi != null ? (data.cpi >= 1 ? "on or above 1.0" : "below 1.0") : "no actual-cost data")}
               subTone={data.cpi != null && data.cpi < 1 ? "rose" : "muted"} />
      <KpiCard label="SPI" value={spi}
               sub={data.spi_subtext ?? (data.spi != null ? (data.spi >= 1 ? "on or above 1.0" : "below 1.0") : "no planned-value data")}
               subTone={data.spi != null && data.spi < 1 ? "amber" : "muted"} />
      <KpiCard label="Open risks" value={String(open)}
               sub={high > 0 ? `${high} high` : "no high-severity"}
               subTone={high > 0 ? "rose" : "muted"} />
      <KpiCard label="Critical tasks"
               value={crit != null ? `${crit} / ${total}` : "—"}
               sub="RCPM" subTone="muted" />
    </section>
  );
}

function KpiCard({ label, value, sub, subTone }: {
  label: string;
  value: string;
  sub: string;
  subTone: "muted" | "amber" | "rose" | "emerald";
}) {
  const subClass = ({
    muted: "text-slate-500",
    amber: "text-amber-600",
    rose: "text-rose-600",
    emerald: "text-emerald-600",
  } as const)[subTone];
  // Date-shaped values (2026-12-03) get a smaller font so they fit at the
  // narrow column width without forcing truncation or a second line.
  const isDate = /^\d{4}-\d{2}-\d{2}$/.test(value);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`mt-1 font-semibold ${isDate ? "text-lg" : "text-2xl"}`}>{value}</div>
      <div className={`text-xs ${subClass}`}>{sub}</div>
    </div>
  );
}

// ---- Middle row ----------------------------------------------------------

function MiddleRow({ data }: { data: Partial<DashboardData> }) {
  return (
    <section className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      <EarnedValueMini pv={data.planned_value_curve ?? []} pctComplete={data.pct_complete ?? null} />
      <PhaseProgressMini rows={data.phase_progress ?? []} />
      <RiskMatrixMini
        matrix={data.risk_matrix ?? null}
        ids={data.risk_matrix_ids ?? null}
        total={data.risk_matrix_total ?? 0}
        openCount={data.open_risk_count ?? 0}
        highCount={data.high_risk_count ?? 0}
      />
    </section>
  );
}

function PanelCard({ title, openHref, children }: {
  title: string;
  openHref?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-medium text-slate-700">{title}</div>
        {openHref && (
          <Link to={openHref} className="text-xs text-blue-600 hover:underline">open →</Link>
        )}
      </div>
      {children}
    </div>
  );
}

function EarnedValueMini({ pv, pctComplete }: {
  pv: DashboardData["planned_value_curve"];
  pctComplete: number | null;
}) {
  // Real planned-value curve from schedule.csv (cumulative scheduled
  // task-days over time). Earned-value uses pct_complete × terminal PV as
  // a today-anchored estimate; actual-cost data is not in canonical so its
  // curve is not drawn.
  if (!pv.length) {
    return (
      <PanelCard title="Earned value" openHref="/earned-value">
        <div className="p-2 text-xs text-slate-500">
          No schedule available — run Rebuild to compute the planned-value curve.
        </div>
      </PanelCard>
    );
  }

  const xs = pv.map((p) => new Date(p.date).getTime());
  const x0 = xs[0];
  const x1 = xs[xs.length - 1];
  const span = Math.max(1, x1 - x0);
  const points = pv
    .map((p) => {
      const x = ((new Date(p.date).getTime() - x0) / span) * 290 + 5;
      const y = 110 - p.cumulative_pct * 100;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  // Earned-value at today: pct_complete × terminal_pv_pct (capped). Plot as
  // a single horizontal segment from project start to today.
  const todayMs = Date.now();
  const showToday = todayMs >= x0 && todayMs <= x1 && pctComplete != null;
  const todayX = showToday ? ((todayMs - x0) / span) * 290 + 5 : null;
  const evY = pctComplete != null ? 110 - pctComplete * 100 : null;

  return (
    <PanelCard title="Earned value" openHref="/earned-value">
      <svg viewBox="0 0 300 120" className="h-32 w-full">
        {/* Planned-value curve from schedule.csv */}
        <polyline fill="none" stroke="#94a3b8" strokeWidth="2" points={points} />
        {/* Earned-value point at today (capped at pct_complete) */}
        {showToday && evY != null && todayX != null && (
          <>
            <line x1="5" y1={evY} x2={todayX} y2={evY}
                  stroke="#0ea5e9" strokeWidth="2" />
            <circle cx={todayX} cy={evY} r="3" fill="#0ea5e9" />
            <line x1={todayX} y1={5} x2={todayX} y2={115}
                  stroke="#cbd5e1" strokeDasharray="2 2" />
          </>
        )}
        <text x="6" y="14" fontSize="9" fill="#64748b">planned</text>
        <text x="6" y="26" fontSize="9" fill="#0ea5e9">earned</text>
      </svg>
      <div className="mt-1 text-[10px] text-slate-400">
        Planned: real schedule. Earned: pct_complete × budget. Actual-cost feed not yet wired.
      </div>
    </PanelCard>
  );
}

function MonteCarloCard({ mc }: { mc: DashboardData["monte_carlo"] | null }) {
  if (!mc || !mc.p50) {
    return (
      <div className="rounded border border-slate-200 p-2 text-xs">
        <div className="font-medium">Monte Carlo · P-curve</div>
        <div className="mt-1 text-slate-500">
          No Monte Carlo run on file — run Rebuild to populate.
        </div>
      </div>
    );
  }

  // The four percentiles span a short window (typically ~3 weeks). Map them
  // into the 0-200 SVG x-coordinate space proportionally.
  const dates: Array<{ k: string; iso: string; t: number; color: string }> = [];
  for (const [k, color] of [
    ["p10", "#10b981"],
    ["p50", "#0ea5e9"],
    ["p80", "#f59e0b"],
    ["p95", "#ef4444"],
  ] as const) {
    const iso = mc[k];
    if (iso) dates.push({ k, iso, t: new Date(iso).getTime(), color });
  }
  const tMin = Math.min(...dates.map((d) => d.t));
  const tMax = Math.max(...dates.map((d) => d.t));
  const span = Math.max(1, tMax - tMin);
  // The cumulative-distribution curve: for each percentile, plot the
  // (date → probability) point and join with a smooth path. Probability
  // values: p10=0.1, p50=0.5, p80=0.8, p95=0.95.
  const probs = { p10: 0.1, p50: 0.5, p80: 0.8, p95: 0.95 } as const;
  const curvePoints = dates.map((d) => {
    const x = 5 + ((d.t - tMin) / span) * 190;
    const y = 55 - probs[d.k as keyof typeof probs] * 50;
    return { ...d, x, y };
  });
  const pathD = curvePoints
    .map((p, i) => (i === 0 ? `M ${p.x.toFixed(1)} 55` : `L ${p.x.toFixed(1)} ${p.y.toFixed(1)}`))
    .join(" ")
    + ` L ${curvePoints[curvePoints.length - 1].x.toFixed(1)} ${(55 - 0.99 * 50).toFixed(1)}`;

  const shortDate = (iso: string) => {
    const d = new Date(iso);
    return `${d.toLocaleString("en-US", { month: "short" })} ${d.getDate()}`;
  };

  return (
    <div className="rounded border border-slate-200 p-2 text-xs">
      <div className="font-medium">Monte Carlo · P-curve</div>
      <svg viewBox="0 0 200 60" className="mt-1 h-14 w-full">
        <path d={pathD} fill="none" stroke="#0ea5e9" strokeWidth="2" />
        {curvePoints.map((p) => (
          <line key={p.k} x1={p.x} y1={5} x2={p.x} y2={55}
                stroke={p.color} strokeDasharray="2 2" />
        ))}
      </svg>
      <div className="mt-1 text-slate-500">
        P50 · P80 · P95 = {mc.p50 ? shortDate(mc.p50) : "?"} · {mc.p80 ? shortDate(mc.p80) : "?"} · {mc.p95 ? shortDate(mc.p95) : "?"}
      </div>
      {mc.simulations && (
        <div className="text-[10px] text-slate-400">{mc.simulations.toLocaleString()} simulations</div>
      )}
    </div>
  );
}

function PhaseProgressMini({ rows }: { rows: DashboardData["phase_progress"] }) {
  if (!rows.length) {
    return (
      <PanelCard title="Phase progress" openHref="/gantt">
        <div className="text-xs text-slate-500">No phases in canonical.</div>
      </PanelCard>
    );
  }
  const shown = rows.slice(0, 6);
  const extra = rows.length - shown.length;
  return (
    <PanelCard title="Phase progress" openHref="/gantt">
      <div className="space-y-1.5 text-xs">
        {shown.map((r, i) => {
          const pct = r.completion_pct == null ? null : Math.round(r.completion_pct * 100);
          const barColor = pct == null
            ? "bg-slate-200"
            : pct >= 100 ? "bg-emerald-500"
            : pct > 0 ? "bg-sky-500"
            : "bg-slate-200";
          const idx = i + 1;
          return (
            <div key={r.id}>
              <div className="flex justify-between">
                <span className="truncate pr-2">{idx}. {r.name}</span>
                <span className="text-slate-500">{pct == null ? "—" : `${pct}%`}</span>
              </div>
              <div className="h-1.5 rounded bg-slate-100">
                {pct != null && pct > 0 && (
                  <div className={`h-full rounded ${barColor}`} style={{ width: `${pct}%` }} />
                )}
              </div>
            </div>
          );
        })}
        {extra > 0 && (
          <div className="text-slate-400">… {extra} more phase{extra === 1 ? "" : "s"}</div>
        )}
      </div>
    </PanelCard>
  );
}

function RiskMatrixMini({ matrix, ids, total, openCount, highCount }: {
  matrix: number[][] | null;
  ids: string[][][] | null;
  total: number;
  openCount: number;
  highCount: number;
}) {
  return (
    <PanelCard title="Risk matrix" openHref="/risk">
      <div className="mb-2 flex items-center gap-2 text-[11px] text-slate-600">
        <span><span className="font-semibold text-slate-900">{openCount}</span> open</span>
        <span className="text-slate-300">·</span>
        <span className={highCount > 0 ? "text-rose-600" : ""}>
          <span className="font-semibold">{highCount}</span> high
        </span>
        <span className="text-slate-300">·</span>
        <span><span className="font-semibold text-slate-900">{total}</span> in matrix</span>
      </div>
      <div className="grid grid-cols-[auto_repeat(5,1fr)] gap-1 text-[10px]">
        <div />
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={`top-${i}`} className="text-center text-slate-500">{i}</div>
        ))}
        {[5, 4, 3, 2, 1].map((l) => (
          <Fragment key={`row-${l}`}>
            <div className="text-right text-slate-500">{l}</div>
            {[1, 2, 3, 4, 5].map((i) => {
              const n = matrix?.[l - 1]?.[i - 1] ?? 0;
              const cellIds = ids?.[l - 1]?.[i - 1] ?? [];
              const sev = l * i;
              const bg = riskCellBackground(sev);
              const fg = sev >= 16 ? "text-white" : "text-slate-900";
              const tip = cellIds.length
                ? `L=${l} · I=${i} — ${cellIds.join(", ")} (click to filter)`
                : `L=${l} · I=${i} — no risks`;
              const filterHref = `/risk?probability=${LEVEL_FROM_NUM[l] ?? ""}&impact=${LEVEL_FROM_NUM[i] ?? ""}`;
              const baseClass = `aspect-square rounded ${bg} ${fg} flex items-center justify-center text-center`;
              if (n > 0) {
                return (
                  <Link key={`cell-${l}-${i}`} to={filterHref} title={tip}
                        className={`${baseClass} cursor-pointer hover:ring-2 hover:ring-slate-900/40`}>
                    {n}
                  </Link>
                );
              }
              return (
                <div key={`cell-${l}-${i}`} title={tip}
                     className={`${baseClass} opacity-90`} />
              );
            })}
          </Fragment>
        ))}
      </div>
      <div className="mt-2 flex justify-between text-[10px] text-slate-500">
        <span>↑ likelihood</span><span>impact →</span>
      </div>
    </PanelCard>
  );
}

// Numeric likelihood/impact axis (1..5) maps back to canonical levels
// (low/medium/high). Canonical only uses these three buckets, so numeric
// rows 2 and 4 are always empty.
const LEVEL_FROM_NUM: Record<number, string> = {
  1: "low",
  3: "medium",
  5: "high",
};

function riskCellBackground(sev: number): string {
  // Mirrors the wireframe palette: emerald (safe) → amber → rose → deep-rose.
  if (sev >= 20) return "bg-rose-700";
  if (sev >= 15) return "bg-rose-500";
  if (sev >= 10) return "bg-rose-400";
  if (sev >= 6) return "bg-amber-300";
  if (sev >= 4) return "bg-amber-200";
  if (sev >= 2) return "bg-emerald-200";
  return "bg-emerald-100";
}

// ---- Bottom row ----------------------------------------------------------

function BottomRow({ data }: { data: Partial<DashboardData> }) {
  return (
    <section className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <TopCriticalMini rows={data.top_critical_tasks ?? []} />
      <RecentChangesMini rows={data.recent_changes ?? []} />
    </section>
  );
}

function TopCriticalMini({ rows }: { rows: DashboardData["top_critical_tasks"] }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-2 text-sm font-medium text-slate-700">Top critical tasks (RCPM)</div>
      {!rows.length ? (
        <div className="text-xs text-slate-500">
          Critical path not yet computed. Click Rebuild in the right pane.
        </div>
      ) : (
        <table className="w-full table-fixed text-xs">
          <colgroup>
            <col className="w-[26%]" />
            <col />
            <col className="w-12" />
            <col className="w-10" />
          </colgroup>
          <thead className="text-slate-500">
            <tr className="border-b border-slate-100">
              <th className="text-left font-normal">id</th>
              <th className="text-left font-normal">task</th>
              <th className="text-right font-normal">slack</th>
              <th className="text-right font-normal">days</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-50 last:border-b-0">
                <td className="truncate py-1 pr-2 font-mono text-slate-700">{r.id}</td>
                <td className="truncate pr-2">{r.name}</td>
                <td className="text-right text-rose-600">{r.slack_days}</td>
                <td className="text-right">{r.duration_days}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RecentChangesMini({ rows }: { rows: DashboardData["recent_changes"] }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-2 text-sm font-medium text-slate-700">Recent canonical changes</div>
      {!rows.length ? (
        <div className="text-xs text-slate-500">
          No git history available in this environment.
        </div>
      ) : (
        <ol className="relative space-y-2 border-l border-slate-200 pl-4 text-xs">
          {rows.map((r) => (
            <li key={r.short}>
              <span className={`absolute -left-[5px] mt-1 inline-block h-2.5 w-2.5 rounded-full ${changeDotColor(r.kind)}`} />
              <span className="text-slate-500">{r.ago}</span> — {r.subject}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function changeDotColor(kind: string): string {
  if (kind === "risk") return "bg-emerald-500";
  if (kind === "task" || kind === "add") return "bg-sky-500";
  if (kind === "schedule") return "bg-slate-400";
  if (kind === "phase") return "bg-amber-500";
  if (kind === "milestone") return "bg-violet-500";
  if (kind === "fix") return "bg-emerald-500";
  if (kind === "remove") return "bg-rose-500";
  return "bg-slate-300";
}

function severityChipClasses(label: string): string {
  const base = "rounded px-1.5 py-0.5";
  if (label === "high") return `${base} bg-rose-100 text-rose-700`;
  if (label === "medium") return `${base} bg-amber-100 text-amber-700`;
  return `${base} bg-emerald-100 text-emerald-700`;
}
